import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ServiceSidebar from './ServiceSidebar';
import './Service.css';
import { API_BASE } from '../../api';
import Loader from '../Loader/Loader';

/**
 * Dynamic service detail page.
 *
 * All content comes from GET /api/services/:slug (the services table, editable
 * in the admin Services tab). The slug comes either from the URL (:slug) or —
 * for legacy paths like /services/ivf — from the ROUTE_ALIASES map.
 *
 * Renders whatever section blocks the admin configured: steps, text, list,
 * grid, faq and seo blocks, in order.
 */

// Legacy hardcoded routes -> slugs. The routes still work; content is DB-driven.
const ROUTE_ALIASES = {
  ivf: 'ivf',
  iui: 'iui',
  icsi: 'icsi',
  oocyte: 'oocyte',
  embryo: 'embryo',
  donor: 'donor',
  gynae: 'gynae',
  pediatrics: 'pediatrics',
};

const isBlank = (v) => v == null || String(v).trim() === '';
const cleanList = (arr) =>
  Array.isArray(arr) ? arr.filter((x) => !isBlank(x)) : [];

/**
 * FAQ items exist in two historical shapes: { q, a } objects (what the admin
 * editor writes) and [q, a] tuples (original seed data). Accept both
 * everywhere — rendering and the FAQPage schema — and drop blank entries.
 */
const normalizeFaqItems = (arr) =>
  (Array.isArray(arr) ? arr : [])
    .map((it) => (Array.isArray(it) ? { q: it[0], a: it[1] } : it))
    .filter((it) => it && !isBlank(it.q) && !isBlank(it.a));

/** Steps have the same two historical shapes: { title, text } and
 * [title, text] tuples. Accept both; keep steps that have either field. */
const normalizeSteps = (arr) =>
  (Array.isArray(arr) ? arr : [])
    .map((s) => (Array.isArray(s) ? { title: s[0], text: s[1] } : s))
    .filter((s) => s && (!isBlank(s.title) || !isBlank(s.text)));

/**
 * Renders one section block — but ONLY when it has real content. A block
 * with no body (no steps / items / text / faq entries) is hidden entirely,
 * even if it carries a title: an empty section reads as broken content.
 */
function SectionBlock({ block }) {
  if (!block || !block.type) return null;
  const title = isBlank(block.title) ? null : String(block.title).trim();

  if (block.type === 'steps') {
    const steps = normalizeSteps(block.steps);
    if (!steps.length) return null;
    return (
      <section className="process-section">
        {title ? <h2 className="section-title">{title}</h2> : null}
        <div className="steps-grid">
          {steps.map((s, i) => (
            <div className="step" key={i}>
              <div className="step-number">{String(i + 1).padStart(2, '0')}</div>
              {!isBlank(s.title) && <h3>{s.title}</h3>}
              {!isBlank(s.text) && <p>{s.text}</p>}
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (block.type === 'list') {
    const items = cleanList(block.items);
    if (!items.length) return null;
    return (
      <section className="why-section">
        {title ? <h2>{title}</h2> : null}
        <ul>
          {items.map((it, i) => (
            <li key={i}>{it}</li>
          ))}
        </ul>
      </section>
    );
  }

  if (block.type === 'grid') {
    const items = cleanList(block.items);
    if (!items.length) return null;
    return (
      <section className="service-grid">
        {title ? <h2>{title}</h2> : null}
        <div className="grid">
          {items.map((it, i) => (
            <div className="card" key={i}>
              {it}
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (block.type === 'text' || block.type === 'seo') {
    const paras = String(block.text || '')
      .split(/\n\s*\n/)
      .map((p) => p.trim())
      .filter(Boolean);
    if (!paras.length) return null;
    return (
      <section>
        {title ? <h2>{title}</h2> : null}
        {paras.map((para, i) => (
          <p key={i}>{para}</p>
        ))}
      </section>
    );
  }

  if (block.type === 'faq') {
    const items = normalizeFaqItems(block.items);
    if (!items.length) return null;
    return <FaqBlock title={title} items={items} />;
  }

  return null;
}

function FaqBlock({ title, items }) {
  const [open, setOpen] = useState(-1);
  return (
    <section className="faq-section">
      {title ? <h2>{title}</h2> : null}
      {items.map((it, i) => (
        <div className="faq-item" key={i}>
          <button
            type="button"
            className="faq-question"
            onClick={() => setOpen(open === i ? -1 : i)}
            aria-expanded={open === i}
          >
            {it.q} <span>{open === i ? '−' : '+'}</span>
          </button>
          {open === i && <div className="faq-answer">{it.a}</div>}
        </div>
      ))}
    </section>
  );
}

const ServiceDetail = () => {
  const params = useParams();
  // /services/:slug or a legacy alias route (/services/ivf etc.)
  const key = (params.slug || '').toLowerCase();
  const slug = ROUTE_ALIASES[key] || key;

  const [service, setService] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ok | notfound | error

  useEffect(() => {
    let mounted = true;
    setStatus('loading');
    setService(null);
    fetch(`${API_BASE}/services/${encodeURIComponent(slug)}`)
      .then(async (res) => {
        if (!mounted) return;
        if (res.status === 404) {
          setStatus('notfound');
          return;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!mounted) return;
        setService(data.service || null);
        setStatus(data.service ? 'ok' : 'notfound');
      })
      .catch(() => {
        if (mounted) setStatus('error');
      });
    return () => {
      mounted = false;
    };
  }, [slug]);

  // ----- Loading / error states -----
  if (status === 'loading') {
    return (
      <div className="service-page">
        <Loader status="Loading service" />
      </div>
    );
  }

  if (status === 'notfound' || status === 'error' || !service) {
    return (
      <div className="service-page">
        <div className="services-layout container" style={{ flexDirection: 'column', padding: '80px 20px', textAlign: 'center' }}>
          <h1>{status === 'notfound' ? 'Service not found' : 'Something went wrong'}</h1>
          <p>
            {status === 'notfound'
              ? 'The service you are looking for does not exist or is not currently visible.'
              : 'We could not load this page right now. Please try again.'}
          </p>
          <Link to="/contact" className="btn" style={{ marginTop: 12 }}>
            Contact us
          </Link>
        </div>
      </div>
    );
  }

  const hero = service.hero || {};
  const sections = Array.isArray(service.sections) ? service.sections : [];

  return (
    <div className="service-page">
      {/* SEO from the DB (page_title / meta_description / canonical / schema) */}
      <Helmet>
        <title>{service.page_title || `${service.name} | Amaanat IVF`}</title>
        <meta name="description" content={service.meta_description || service.description || ''} />
        <link rel="canonical" href={service.canonical_url || `/services/${service.slug}`} />

        {/* Medical schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': service.schema_type || 'MedicalProcedure',
            name: service.name,
            description: service.meta_description || service.description || '',
            location: {
              '@type': 'MedicalClinic',
              name: 'Amaanat IVF',
              address: {
                '@type': 'PostalAddress',
                addressLocality: 'Ambala',
                addressCountry: 'India',
              },
            },
          })}
        </script>

        {/* FAQ schema built from the faq sections */}
        {(() => {
          const faqItems = sections
            .filter((b) => b.type === 'faq')
            .flatMap((b) => normalizeFaqItems(b.items));
          if (!faqItems.length) return null;
          return (
            <script type="application/ld+json">
              {JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'FAQPage',
                mainEntity: faqItems.map((it) => ({
                  '@type': 'Question',
                  name: it.q,
                  acceptedAnswer: { '@type': 'Answer', text: it.a },
                })),
              })}
            </script>
          );
        })()}
      </Helmet>

      {/* HERO */}
      <section className="service-hero">
        <div className="container">
          <h1 className="hero-title">{hero.title || service.name}</h1>
          {hero.subtitle ? <p className="hero-subtitle">{hero.subtitle}</p> : null}
          {hero.stat_value ? (
            <div className="hero-stats">
              <span>{hero.stat_value}</span> {hero.stat_label || ''}
            </div>
          ) : null}
        </div>
      </section>

      {/* MAIN */}
      <div className="services-layout container">
        <ServiceSidebar />

        <div className="services-content">
          {sections.map((block, i) => (
            <SectionBlock block={block} key={i} />
          ))}

          {/* CTA footer */}
          <section className="service-cta">
            <h2>Book a Consultation</h2>
            <p>Talk to our fertility specialists about the right treatment for you.</p>
            <Link to="/bookappointment" className="btn">
              Book Appointment
            </Link>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ServiceDetail;
